/**
 * StepImagePrep - Step 1: Image Preparation
 *
 * Allows users to:
 * - Preview the image with zoom
 * - Rotate the image (90, 180, 270 degrees)
 * - Crop the image
 * - Apply enhancements (contrast, brightness, grayscale)
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  SnippetItem,
  SnippetDraftData,
  rotateSnippet,
  cropSnippet,
  enhanceSnippetImage,
  runSnippetOCR,
} from "../../../api/snippets";
import { useMediaUrl, invalidateMediaCache } from "../../../hooks/useMediaUrl";

interface StepImagePrepProps {
  snippet: SnippetItem;
  draftData: SnippetDraftData;
  updateDraft: (updates: Partial<SnippetDraftData>) => void;
  imageKey: number;
  onImageEdited: () => void;
}

export function StepImagePrep({
  snippet,
  draftData,
  updateDraft,
  imageKey,
  onImageEdited,
}: StepImagePrepProps) {
  const { getAccessTokenSilently } = useAuth0();

  // Image loading
  const { url: imageUrl, isLoading: imageLoading } = useMediaUrl(
    snippet.id,
    snippet.file_size + imageKey // Cache buster
  );

  // Crop state
  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const cropImageRef = useRef<HTMLImageElement>(null);

  // Operation state
  const [isRotating, setIsRotating] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isRunningOCR, setIsRunningOCR] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Enhancement sliders
  const [contrast, setContrast] = useState(1.0);
  const [brightness, setBrightness] = useState(1.0);
  const [sharpness, setSharpness] = useState(1.0);
  const [grayscale, setGrayscale] = useState(false);

  // Clear messages after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Handle rotation
  const handleRotate = useCallback(
    async (degrees: 90 | 180 | 270) => {
      setIsRotating(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        await rotateSnippet(snippet.id, degrees, token);
        
        invalidateMediaCache(snippet.id);
        const totalRotation = ((draftData.rotation || 0) + degrees) % 360;
        updateDraft({ rotation: totalRotation || null });
        onImageEdited();
        setSuccess(`Rotated ${degrees}°`);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to rotate");
      } finally {
        setIsRotating(false);
      }
    },
    [snippet.id, draftData.rotation, updateDraft, onImageEdited, getAccessTokenSilently]
  );

  // Handle crop
  const handleCropApply = useCallback(async () => {
    if (!completedCrop || !imageNaturalSize) return;

    setIsCropping(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      
      // Convert pixel crop to percentage
      const cropPercent = {
        x: (completedCrop.x / imageNaturalSize.width) * 100,
        y: (completedCrop.y / imageNaturalSize.height) * 100,
        width: (completedCrop.width / imageNaturalSize.width) * 100,
        height: (completedCrop.height / imageNaturalSize.height) * 100,
      };

      await cropSnippet(snippet.id, cropPercent, token);
      
      invalidateMediaCache(snippet.id);
      updateDraft({ crop_applied: true });
      setCropMode(false);
      setCrop(undefined);
      setCompletedCrop(undefined);
      onImageEdited();
      setSuccess("Image cropped");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to crop");
    } finally {
      setIsCropping(false);
    }
  }, [snippet.id, completedCrop, imageNaturalSize, updateDraft, onImageEdited, getAccessTokenSilently]);

  // Handle enhancements
  const handleApplyEnhancements = useCallback(async () => {
    setIsEnhancing(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await enhanceSnippetImage(
        snippet.id,
        {
          contrast: contrast !== 1.0 ? contrast : undefined,
          brightness: brightness !== 1.0 ? brightness : undefined,
          sharpness: sharpness !== 1.0 ? sharpness : undefined,
          grayscale,
          auto_orient: true,
        },
        token
      );

      invalidateMediaCache(snippet.id);
      updateDraft({
        enhancements: { contrast, brightness, sharpness, grayscale, auto_orient: true },
      });
      
      // Reset sliders since changes are now permanently applied
      setContrast(1.0);
      setBrightness(1.0);
      setSharpness(1.0);
      setGrayscale(false);
      
      onImageEdited();
      setSuccess("Enhancements saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply enhancements");
    } finally {
      setIsEnhancing(false);
    }
  }, [snippet.id, contrast, brightness, sharpness, grayscale, updateDraft, onImageEdited, getAccessTokenSilently]);

  // Handle OCR
  const handleRunOCR = useCallback(async () => {
    setIsRunningOCR(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await runSnippetOCR(snippet.id, token);
      setSuccess("Text extracted successfully!");
      // The snippet will be re-fetched by the parent component
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to extract text");
    } finally {
      setIsRunningOCR(false);
    }
  }, [snippet.id, getAccessTokenSilently]);

  // Handle crop image load
  const onCropImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    setImageNaturalSize({ width: naturalWidth, height: naturalHeight });

    // Set initial crop to center
    const initialCrop = centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, naturalWidth / naturalHeight, naturalWidth, naturalHeight),
      naturalWidth,
      naturalHeight
    );
    setCrop(initialCrop);
  }, []);

  const hasEnhancementChanges =
    contrast !== 1.0 || brightness !== 1.0 || sharpness !== 1.0 || grayscale;

  // Generate CSS filter string for real-time preview
  const previewFilterStyle = {
    filter: [
      contrast !== 1.0 ? `contrast(${contrast})` : "",
      brightness !== 1.0 ? `brightness(${brightness})` : "",
      grayscale ? "grayscale(1)" : "",
      // Note: CSS doesn't have a direct sharpness filter, but we can hint with contrast
      sharpness > 1.5 ? `contrast(${1 + (sharpness - 1) * 0.1})` : "",
    ]
      .filter(Boolean)
      .join(" ") || "none",
  };

  return (
    <div className="space-y-6">
      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Image preview */}
        <div className="lg:col-span-2">
          <div className="bg-vault-100 rounded-lg p-4 min-h-[400px] flex items-center justify-center">
            {imageLoading ? (
              <div className="flex flex-col items-center gap-2 text-vault-500">
                <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Loading image...</span>
              </div>
            ) : cropMode && imageUrl ? (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                className="max-w-full"
              >
                <img
                  ref={cropImageRef}
                  src={imageUrl}
                  alt="Crop preview"
                  onLoad={onCropImageLoad}
                  className="max-w-full max-h-[500px] object-contain"
                />
              </ReactCrop>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={snippet.original_filename}
                className="max-w-full max-h-[500px] object-contain rounded shadow-lg transition-all duration-150"
                style={previewFilterStyle}
              />
            ) : (
              <div className="text-vault-400">No image available</div>
            )}
          </div>

          {/* Crop mode actions */}
          {cropMode && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <button
                onClick={() => {
                  setCropMode(false);
                  setCrop(undefined);
                  setCompletedCrop(undefined);
                }}
                className="px-4 py-2 text-vault-600 hover:bg-vault-100 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCropApply}
                disabled={!completedCrop || isCropping}
                className="px-4 py-2 bg-vault-600 text-white hover:bg-vault-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCropping ? "Cropping..." : "Apply Crop"}
              </button>
            </div>
          )}
        </div>

        {/* Tools panel */}
        <div className="space-y-6">
          {/* Rotation */}
          <div className="bg-white border border-vault-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-vault-800 mb-3">Rotate</h3>
            <div className="flex gap-2">
              <button
                onClick={() => handleRotate(270)}
                disabled={isRotating || cropMode}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-vault-100 hover:bg-vault-200 rounded-lg text-vault-700 transition-colors disabled:opacity-50"
                title="Rotate left 90°"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                </svg>
              </button>
              <button
                onClick={() => handleRotate(180)}
                disabled={isRotating || cropMode}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-vault-100 hover:bg-vault-200 rounded-lg text-vault-700 transition-colors disabled:opacity-50"
                title="Rotate 180°"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </button>
              <button
                onClick={() => handleRotate(90)}
                disabled={isRotating || cropMode}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-vault-100 hover:bg-vault-200 rounded-lg text-vault-700 transition-colors disabled:opacity-50"
                title="Rotate right 90°"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
                </svg>
              </button>
            </div>
            {isRotating && (
              <p className="text-xs text-vault-500 mt-2 text-center">Rotating...</p>
            )}
          </div>

          {/* Crop */}
          <div className="bg-white border border-vault-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-vault-800 mb-3">Crop</h3>
            <button
              onClick={() => setCropMode(!cropMode)}
              disabled={isRotating}
              className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                cropMode
                  ? "bg-vault-600 text-white"
                  : "bg-vault-100 hover:bg-vault-200 text-vault-700"
              } disabled:opacity-50`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {cropMode ? "Exit Crop Mode" : "Enter Crop Mode"}
            </button>
            {draftData.crop_applied && (
              <p className="text-xs text-green-600 mt-2 text-center">Image has been cropped</p>
            )}
          </div>

          {/* Enhancements */}
          <div className="bg-white border border-vault-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-vault-800 mb-3">Enhance</h3>
            <div className="space-y-4">
              {/* Contrast */}
              <div>
                <label className="flex items-center justify-between text-sm text-vault-600 mb-1">
                  <span>Contrast</span>
                  <span className="text-vault-400">{contrast.toFixed(1)}</span>
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={contrast}
                  onChange={(e) => setContrast(parseFloat(e.target.value))}
                  disabled={cropMode}
                  className="w-full accent-vault-600"
                />
              </div>

              {/* Brightness */}
              <div>
                <label className="flex items-center justify-between text-sm text-vault-600 mb-1">
                  <span>Brightness</span>
                  <span className="text-vault-400">{brightness.toFixed(1)}</span>
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value={brightness}
                  onChange={(e) => setBrightness(parseFloat(e.target.value))}
                  disabled={cropMode}
                  className="w-full accent-vault-600"
                />
              </div>

              {/* Sharpness */}
              <div>
                <label className="flex items-center justify-between text-sm text-vault-600 mb-1">
                  <span>Sharpness</span>
                  <span className="text-vault-400">{sharpness.toFixed(1)}</span>
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="3.0"
                  step="0.1"
                  value={sharpness}
                  onChange={(e) => setSharpness(parseFloat(e.target.value))}
                  disabled={cropMode}
                  className="w-full accent-vault-600"
                />
              </div>

              {/* Grayscale */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={grayscale}
                  onChange={(e) => setGrayscale(e.target.checked)}
                  disabled={cropMode}
                  className="rounded border-vault-300 text-vault-600 focus:ring-vault-500"
                />
                <span className="text-sm text-vault-600">Convert to grayscale</span>
              </label>

              {/* Preview indicator */}
              {hasEnhancementChanges && (
                <p className="text-xs text-amber-600 text-center">
                  Preview shown above. Click below to save changes.
                </p>
              )}

              {/* Apply button */}
              <button
                onClick={handleApplyEnhancements}
                disabled={!hasEnhancementChanges || isEnhancing || cropMode}
                className="w-full px-4 py-2 bg-vault-600 text-white hover:bg-vault-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEnhancing ? "Saving..." : "Save Enhancements"}
              </button>
            </div>
          </div>

          {/* OCR - Text Extraction */}
          <div className="bg-white border border-vault-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-vault-800 mb-3">Extract Text (OCR)</h3>
            {snippet.transcription ? (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs text-green-700 mb-1 font-medium">✓ Text extracted</p>
                  <p className="text-xs text-green-600">{snippet.transcription.length} characters</p>
                </div>
                <button
                  onClick={handleRunOCR}
                  disabled={isRunningOCR || cropMode}
                  className="w-full px-4 py-2 bg-vault-100 text-vault-700 hover:bg-vault-200 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {isRunningOCR ? "Extracting..." : "Re-run OCR"}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-vault-500">
                  Extract text from this image using OCR. Useful for documents, screenshots, and printed text.
                </p>
                <button
                  onClick={handleRunOCR}
                  disabled={isRunningOCR || cropMode}
                  className="w-full px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRunningOCR ? "Extracting..." : "Extract Text"}
                </button>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="text-xs text-vault-500 space-y-1">
            <p>Original file: {snippet.original_filename}</p>
            <p>Size: {(snippet.file_size / 1024).toFixed(1)} KB</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StepImagePrep;
